import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa';
import { EyeOutlined, EditOutlined, DeleteOutlined, UndoOutlined } from '@ant-design/icons';
import { Table, Tag, message, Modal, Descriptions, Space } from 'antd';
import { BaseInput, BaseSelect, BaseButton } from '../../components/ui';
import Swal from 'sweetalert2';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import ModulePermissionGrid from '../../components/ModulePermissionGrid';
import usuarioService from '../../services/usuarioService';
import { useAuth } from '../../utils/AuthContext';
import { MODULE_LABELS, ROLE_DEFAULT_PERMISSIONS } from '../../config/tenantPermissions';
import { ROLE_LABELS, TENANT_ROLES } from '../../config/tenantRoles';
import '../../assets/styles/table-styles.css';

const ROLE_COLORS = {
  administrador: 'red',
  propietario: 'blue',
  operador: 'orange',
  inquilino: 'green',
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-PE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const ESTADO_FILTRO_OPTIONS = [
  { value: 'activos', label: 'Activos' },
  { value: 'inactivos', label: 'Inactivos' },
  { value: 'todos', label: 'Todos' },
];

const UsuariosRegistros = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('activos');
  const [viewModal, setViewModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editRol, setEditRol] = useState('');
  const [editPermisos, setEditPermisos] = useState([]);
  const [saving, setSaving] = useState(false);

  const cargarUsuarios = useCallback(async () => {
    setLoading(true);
    try {
      const data = await usuarioService.listarUsuarios(estadoFiltro);
      setUsuarios(Array.isArray(data) ? data : []);
    } catch {
      message.error('No se pudieron cargar los usuarios del dominio');
    } finally {
      setLoading(false);
    }
  }, [estadoFiltro]);

  useEffect(() => {
    cargarUsuarios();
  }, [cargarUsuarios]);

  const filtrados = useMemo(() => {
    const term = filtro.trim().toLowerCase();
    if (!term) return usuarios;

    return usuarios.filter((item) => {
      const persona = item.persona || {};
      const campos = [
        item.username,
        persona.nombre,
        persona.apellido,
        persona.email,
        persona.dni,
        persona.rol,
      ];
      return campos.some((c) => String(c || '').toLowerCase().includes(term));
    });
  }, [usuarios, filtro]);

  const handleView = (record) => {
    setSelectedUser(record);
    setViewModal(true);
  };

  const handleEdit = (record) => {
    setSelectedUser(record);
    setEditRol(record.persona?.rol || '');
    setEditPermisos(record.permisos || []);
    setEditModal(true);
  };

  const handleEditRolChange = (rol) => {
    setEditRol(rol);
    setEditPermisos(ROLE_DEFAULT_PERMISSIONS[rol] || []);
  };

  const toggleEditPermission = (module) => {
    setEditPermisos((prev) =>
      prev.includes(module) ? prev.filter((m) => m !== module) : [...prev, module]
    );
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;

    setSaving(true);
    try {
      const data = await usuarioService.actualizarUsuario(selectedUser.id, {
        rol: editRol,
        permisos: editPermisos,
      });

      const usuario = data.usuario;

      setUsuarios((prev) =>
        prev.map((u) => (u.id === selectedUser.id ? (usuario || u) : u))
      );
      message.success('Usuario actualizado correctamente');
      setEditModal(false);
      setSelectedUser(null);
    } catch (error) {
      message.error(error.response?.data?.mensaje || 'No se pudo actualizar el usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    const esPropio = authUser?.userId === record.id || authUser?.username === record.username;

    if (esPropio) {
      message.warning('No puede desactivar su propia cuenta');
      return;
    }

    Swal.fire({
      title: '¿Desactivar usuario?',
      html: `La cuenta <strong>${record.username}</strong> quedará inactiva y no podrá iniciar sesión. Los datos se conservan en el sistema.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Sí, desactivar',
      cancelButtonText: 'Cancelar',
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        await usuarioService.desactivarUsuario(record.id);
        await cargarUsuarios();
        message.success('Usuario desactivado correctamente');
      } catch (error) {
        message.error(error.response?.data?.mensaje || 'No se pudo desactivar el usuario');
      }
    });
  };

  const handleRestore = (record) => {
    Swal.fire({
      title: '¿Reactivar usuario?',
      html: `La cuenta <strong>${record.username}</strong> volverá a estar activa y podrá iniciar sesión.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#16a34a',
      confirmButtonText: 'Sí, reactivar',
      cancelButtonText: 'Cancelar',
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        await usuarioService.reactivarUsuario(record.id);
        await cargarUsuarios();
        message.success('Usuario reactivado correctamente');
      } catch (error) {
        message.error(error.response?.data?.mensaje || 'No se pudo reactivar el usuario');
      }
    });
  };

  const isInactivo = (record) => record.activo === false || Boolean(record.deleted_at);

  const columns = [
    {
      title: 'Usuario',
      dataIndex: 'username',
      key: 'username',
      render: (username) => <strong>{username}</strong>,
    },
    {
      title: 'Nombre completo',
      key: 'nombre',
      render: (_, record) => {
        const p = record.persona || {};
        return [p.nombre, p.apellido].filter(Boolean).join(' ') || '—';
      },
    },
    {
      title: 'Email',
      key: 'email',
      render: (_, record) => record.persona?.email || '—',
    },
    {
      title: 'DNI',
      key: 'dni',
      render: (_, record) => record.persona?.dni || '—',
    },
    {
      title: 'Rol',
      key: 'rol',
      render: (_, record) => {
        const rol = record.persona?.rol || '';
        return (
          <Tag color={ROLE_COLORS[rol] || 'default'}>
            {ROLE_LABELS[rol] || rol || '—'}
          </Tag>
        );
      },
    },
    {
      title: 'Módulos',
      key: 'permisos',
      render: (_, record) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 220 }}>
          {(record.permisos || []).slice(0, 3).map((mod) => (
            <Tag key={mod} color="processing">
              {MODULE_LABELS[mod] || mod}
            </Tag>
          ))}
          {(record.permisos || []).length > 3 && (
            <Tag>+{(record.permisos || []).length - 3}</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 100,
      render: (_, record) => (
        <Tag color={isInactivo(record) ? 'default' : 'success'}>
          {isInactivo(record) ? 'Inactivo' : 'Activo'}
        </Tag>
      ),
    },
    {
      title: 'Registrado',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value) => formatDate(value),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      fixed: 'right',
      width: 160,
      render: (_, record) => {
        const esPropio = authUser?.userId === record.id || authUser?.username === record.username;
        const inactivo = isInactivo(record);

        return (
          <div className="d-flex">
            <BaseButton
              type="button"
              variant="primary"
              size="sm"
              className="me-2"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
              title="Ver detalle"
            />
            {inactivo ? (
              <BaseButton
                type="button"
                variant="success"
                size="sm"
                icon={<UndoOutlined />}
                onClick={() => handleRestore(record)}
                title="Reactivar cuenta"
              />
            ) : (
              <>
                <BaseButton
                  type="button"
                  variant="warning"
                  size="sm"
                  className="me-2"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                  title="Editar permisos"
                />
                <BaseButton
                  type="button"
                  variant="danger"
                  size="sm"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record)}
                  title="Desactivar cuenta"
                  disabled={esPropio}
                />
              </>
            )}
          </div>
        );
      },
    },
  ];

  const persona = selectedUser?.persona || {};

  return (
    <div>
      <Header />
      <Sidebar activeClassName="usuarios-registros" />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="#">Usuarios</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <FaChevronRight />
                  </li>
                  <li className="breadcrumb-item active">Listado</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                    <div>
                      <h4 className="mb-1">Usuarios del dominio</h4>
                      <p className="text-muted mb-0">
                        Cuentas de acceso creadas para este tenant.
                      </p>
                    </div>
                    <BaseButton
                      type="button"
                      variant="primary"
                      onClick={() => navigate('/persona-registrar')}
                    >
                      Registrar usuario
                    </BaseButton>
                  </div>

                  <Space wrap style={{ marginBottom: 16 }}>
                    <BaseInput
                      placeholder="Buscar por nombre, usuario, email o DNI..."
                      value={filtro}
                      onChange={(e) => setFiltro(e.target.value)}
                      wrapperClassName="usuarios-filtro-buscar"
                      style={{ width: 320 }}
                    />
                    <BaseSelect
                      native={false}
                      value={estadoFiltro}
                      onChange={(e) => setEstadoFiltro(e.target.value)}
                      options={ESTADO_FILTRO_OPTIONS}
                      isSearchable={false}
                      wrapperClassName="usuarios-filtro-estado"
                    />
                  </Space>

                  <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={filtrados}
                    loading={loading}
                    pagination={{ pageSize: 10, showSizeChanger: true }}
                    scroll={{ x: 1180 }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Detalle del usuario"
        open={viewModal}
        onCancel={() => setViewModal(false)}
        footer={null}
        width={640}
      >
        {selectedUser && (
          <>
            <Descriptions bordered column={1} size="small" className="mb-3">
              <Descriptions.Item label="Usuario">{selectedUser.username}</Descriptions.Item>
              <Descriptions.Item label="Nombre">
                {[persona.nombre, persona.apellido].filter(Boolean).join(' ') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">{persona.email || '—'}</Descriptions.Item>
              <Descriptions.Item label="DNI">{persona.dni || '—'}</Descriptions.Item>
              <Descriptions.Item label="Teléfono">{persona.telefono || '—'}</Descriptions.Item>
              <Descriptions.Item label="Dirección">{persona.direccion || '—'}</Descriptions.Item>
              <Descriptions.Item label="Rol">
                {ROLE_LABELS[persona.rol] || persona.rol || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Estado">
                {isInactivo(selectedUser) ? 'Inactivo' : 'Activo'}
              </Descriptions.Item>
              <Descriptions.Item label="Registrado">
                {formatDate(selectedUser.created_at)}
              </Descriptions.Item>
            </Descriptions>
            <div>
              <strong>Módulos asignados</strong>
              <div className="mt-2 d-flex flex-wrap gap-1">
                {(selectedUser.permisos || []).map((mod) => (
                  <Tag key={mod} color="processing">
                    {MODULE_LABELS[mod] || mod}
                  </Tag>
                ))}
              </div>
            </div>
          </>
        )}
      </Modal>

      <Modal
        title={`Editar usuario: ${selectedUser?.username || ''}`}
        open={editModal}
        onCancel={() => setEditModal(false)}
        onOk={handleSaveEdit}
        okText="Guardar cambios"
        cancelText="Cancelar"
        confirmLoading={saving}
        width={720}
      >
        <BaseSelect
          label="Rol"
          native={false}
          value={editRol || ''}
          onChange={(e) => handleEditRolChange(e.target.value)}
          placeholder="Seleccione un rol"
          options={TENANT_ROLES.map(({ value, label }) => ({ value, label }))}
          helperText="Al cambiar el rol se actualizan los módulos sugeridos. Puede ajustarlos libremente."
        />
        <div className="mt-3">
          <label className="form-label fw-semibold">Acceso a módulos</label>
          <ModulePermissionGrid selected={editPermisos} onToggle={toggleEditPermission} />
        </div>
      </Modal>
    </div>
  );
};

export default UsuariosRegistros;
